import { useCallback, useContext, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import * as dripIdl from "../idl/drip_vault_v2.json";
import { DripVaultDescriptor } from "../constants/dripVaults";
import {
  DripVaultState,
  dripProgramFor,
  fetchDripVaultState,
  fetchSolPrice,
} from "../features/drip/dripFetch";
import { useDripVaults } from "./useDripVaults";

/**
 * State for every enabled Drip vault in one pass, so the dashboard can total
 * TVL across vaults and render a card per vault. Vaults whose account cannot
 * be read are omitted rather than rendered blank.
 */
export const useDripVaultStates = () => {
  const { connection } = useConnection();
  const { dripProgramAddress } = useContext(AppConfigContext);
  const vaults = useDripVaults();

  const [vaultStates, setVaultStates] = useState<DripVaultState[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVaultStates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const programId = new PublicKey(dripProgramAddress);
      const program = dripProgramFor(dripIdl, dripProgramAddress, connection);
      // One price request for all vaults.
      const solPrice = await fetchSolPrice();

      const results = await Promise.all(
        vaults.map((vault: DripVaultDescriptor) =>
          fetchDripVaultState(program, connection, vault, programId, solPrice)
        )
      );

      setVaultStates(results.filter((v): v is DripVaultState => v !== null));
    } catch (err) {
      console.error("Error fetching drip vault states:", err);
      setError("Error fetching Drip vault state.");
    } finally {
      setIsLoading(false);
    }
  }, [connection, dripProgramAddress, vaults]);

  return { vaultStates, isLoading, error, fetchVaultStates };
};
